import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";

import { useUpdateEdiFileMappingMutation } from "../../../store/queries/ediConversion";
import {
  EdiClaimType,
  IEdiConversionFile,
  IEdiUploadMapping,
  IPayerTaxonomyRule,
  PlaceOfServiceCategory,
} from "../../../types/ediConversionFile";
import { IProvider, SpecialtyList } from "../../../types/Provider/Provider";
import { validateEdiMappingSet } from "./ediMappingValidation";

// Kept in sync with the upload dialog's constants (EdiConversionFilesTable). Small, stable enums —
// duplicated here so the edit modal is self-contained and the critical upload path is untouched.
const EDI_CLAIM_TYPE_OPTIONS: { value: EdiClaimType; label: string }[] = [
  { value: "INSTITUTIONAL", label: "Institutional" },
  { value: "PROFESSIONAL_ANESTHESIA", label: "Professional — Anesthesia" },
  { value: "PROFESSIONAL_PROVIDER", label: "Professional — Provider" },
];

const FACILITY_POS_CATEGORIES: PlaceOfServiceCategory[] = [
  "Inpatient",
  "Outpatient Facility",
];
const NON_FACILITY_POS_CATEGORIES: PlaceOfServiceCategory[] = ["Non-Facility"];

// Only the Professional — Provider rule gets a place-of-service + taxonomy sub-filter.
const posFilterAppliesTo = (t: EdiClaimType) => t === "PROFESSIONAL_PROVIDER";

// Provider specialties pickable for a given claim type (mirrors the backend's validateUploadMappings
// and the upload dialog's allowedSpecialtiesForClaimType).
const allowedSpecialtiesForClaimType = (
  claimType: EdiClaimType
): SpecialtyList[] => {
  if (claimType === "INSTITUTIONAL") return [SpecialtyList.FACILITY];
  if (claimType === "PROFESSIONAL_ANESTHESIA")
    return [SpecialtyList.FACILITY, SpecialtyList.ANESTHESIOLOGIST];
  return [
    SpecialtyList.FACILITY,
    SpecialtyList.PATHOLOGIST,
    SpecialtyList.PRIMARY_PROVIDER,
    SpecialtyList.ASSISTANT_PROVIDER,
  ];
};

// Seed a mapping from an existing file row (its providerId / claimType columns + metadata).
const mappingFromFile = (file: IEdiConversionFile): IEdiUploadMapping => {
  const md = file.metadata ?? {};
  return {
    // Legacy single-provider rows have a null claimType; default them to Professional — Provider so
    // the form has a concrete value (the backend applies the same routing for a null claimType row).
    claimType: (file.claimType ?? "PROFESSIONAL_PROVIDER") as EdiClaimType,
    providerId: file.providerId ?? "",
    placeOfServiceFilter: md.placeOfServiceFilter ?? [],
    excludeMidlevels: md.excludeMidlevels ?? false,
    excludeBehavioralHealth: md.excludeBehavioralHealth ?? false,
    payerTaxonomyRules: md.payerTaxonomyRules ?? [],
  };
};

interface IEditMappingModalProps {
  file: IEdiConversionFile | null;
  visible: boolean;
  onHide: () => void;
  providers: IProvider[];
  payerOptions: { value: number; label: string }[];
  taxonomyOptions: { value: string; label: string }[];
  // The mapping rules of the OTHER rows in this file's upload batch (CLE-2424 cardinality is enforced
  // across the whole batch). Used to disable already-used claim types and to validate the edited set.
  siblingMappings: IEdiUploadMapping[];
  onSaved: (fileId: string, displayName: string) => void;
  onError: (message: string) => void;
}

// CLE-2479 — edit the provider-mapping of an already-processed EDI file. Reuses the same routing
// controls as upload (claim type → provider, place of service, taxonomy filter) for a single row.
// Saving persists the mapping only; the caller prompts to reprocess so it's applied to claims.
function EditMappingModal({
  file,
  visible,
  onHide,
  providers,
  payerOptions,
  taxonomyOptions,
  siblingMappings,
  onSaved,
  onError,
}: IEditMappingModalProps) {
  const [mapping, setMapping] = useState<IEdiUploadMapping | null>(null);
  const [updateMapping, { isLoading: saving }] =
    useUpdateEdiFileMappingMutation();

  // Reseed whenever a different file is opened.
  useEffect(() => {
    setMapping(file ? mappingFromFile(file) : null);
  }, [file]);

  if (!mapping) return null;

  const patch = (changes: Partial<IEdiUploadMapping>) =>
    setMapping((prev) => (prev ? { ...prev, ...changes } : prev));

  const usedBySiblings = siblingMappings.map((s) => s.claimType);
  const typeOptions = EDI_CLAIM_TYPE_OPTIONS.map((o) => ({
    ...o,
    // PROFESSIONAL_PROVIDER may repeat (POS-split, CLE-2424); the other types map once per batch.
    disabled:
      o.value !== "PROFESSIONAL_PROVIDER" && usedBySiblings.includes(o.value),
  }));

  const allowed = allowedSpecialtiesForClaimType(mapping.claimType);
  const providerOpts = providers
    .filter((p) => allowed.includes(p.specialty))
    .map((p) => ({ label: p.name, value: p.id }));

  const showPos = posFilterAppliesTo(mapping.claimType);
  const posFilter = mapping.placeOfServiceFilter ?? [];
  const taxRules = mapping.payerTaxonomyRules ?? [];
  const setTaxRules = (rules: IPayerTaxonomyRule[]) =>
    patch({ payerTaxonomyRules: rules });

  const mappingSetError = validateEdiMappingSet([...siblingMappings, mapping]);
  const canSave = !!mapping.providerId && !mappingSetError && !saving;

  const handleSave = async () => {
    if (!file || !canSave) return;
    // The POS + taxonomy sub-filters only apply to the generic-provider rule; clear them otherwise
    // (the backend enforces this too).
    const normalized: IEdiUploadMapping = showPos
      ? mapping
      : {
          ...mapping,
          placeOfServiceFilter: [],
          excludeMidlevels: undefined,
          excludeBehavioralHealth: undefined,
          payerTaxonomyRules: [],
        };
    try {
      await updateMapping({ id: file.id, mapping: normalized }).unwrap();
      onSaved(file.id, file.displayName);
      onHide();
    } catch (error: any) {
      onError(error?.data?.message || "Failed to update mapping");
    }
  };

  const footer = (
    <div>
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={saving}
      />
      <Button
        label={saving ? "Saving…" : "Save mapping"}
        icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
        onClick={handleSave}
        disabled={!canSave}
      />
    </div>
  );

  return (
    <Dialog
      header={file ? `Edit mapping — ${file.displayName}` : "Edit mapping"}
      visible={visible}
      onHide={() => {
        if (!saving) onHide();
      }}
      footer={footer}
      style={{ width: "min(760px, 95vw)" }}
      // Roomy content area so the dropdown/multiselect panels have space to open below their inputs
      // instead of feeling cramped against the footer.
      contentStyle={{ minHeight: "min(55vh, 360px)" }}
      modal
      closable={!saving}
      blockScroll
    >
      <small
        className="text-muted"
        style={{ display: "block", marginBottom: "1rem" }}
      >
        Change which provider (and, for professional claims, place of service
        and specialty filter) this file&apos;s claims route to. Saving updates
        the mapping only — the existing claims keep their current provider until
        you reprocess the file.
      </small>

      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          alignItems: "flex-start",
          marginBottom: "0.5rem",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: "0.5rem",
            }}
          >
            Claim type
          </label>
          <Dropdown
            value={mapping.claimType}
            options={typeOptions}
            optionDisabled="disabled"
            onChange={(e) =>
              setMapping({
                claimType: e.value as EdiClaimType,
                providerId: "",
                placeOfServiceFilter: [],
              })
            }
            className="w-full"
            disabled={saving}
            appendTo={document.body}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: "0.5rem",
            }}
          >
            Provider
          </label>
          <Dropdown
            value={mapping.providerId}
            options={providerOpts}
            onChange={(e) => patch({ providerId: e.value })}
            placeholder="Select provider"
            className="w-full"
            filter
            showClear
            disabled={saving}
            appendTo={document.body}
          />
        </div>
      </div>

      {showPos && (
        <div style={{ marginTop: "0.5rem" }}>
          <small
            className="text-muted"
            style={{ display: "block", marginBottom: "0.25rem" }}
          >
            Place of service (leave both unchecked to keep all):
          </small>
          <div style={{ display: "flex", gap: "1rem" }}>
            {[
              {
                key: "facility",
                label: "Facility",
                cats: FACILITY_POS_CATEGORIES,
              },
              {
                key: "non-facility",
                label: "Non-Facility",
                cats: NON_FACILITY_POS_CATEGORIES,
              },
            ].map((opt) => {
              const checked = opt.cats.some((c) => posFilter.includes(c));
              return (
                <div
                  key={opt.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <Checkbox
                    inputId={`edit-pos-${opt.key}`}
                    checked={checked}
                    disabled={saving}
                    onChange={(e) => {
                      const without = posFilter.filter(
                        (c) => !opt.cats.includes(c)
                      );
                      patch({
                        placeOfServiceFilter: e.checked
                          ? [...without, ...opt.cats]
                          : without,
                      });
                    }}
                  />
                  <label htmlFor={`edit-pos-${opt.key}`}>{opt.label}</label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showPos && (
        <div
          style={{
            marginTop: "0.75rem",
            paddingTop: "0.75rem",
            borderTop: "1px solid var(--surface-200, #e5e7eb)",
          }}
        >
          <small
            className="text-muted"
            style={{ display: "block", marginBottom: "0.5rem" }}
          >
            Specialty (taxonomy) filter — optional. Leave unconfigured to price
            every professional claim.
          </small>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              marginBottom: "0.5rem",
            }}
          >
            <Checkbox
              inputId="edit-exclude-midlevels"
              checked={mapping.excludeMidlevels ?? false}
              disabled={saving}
              onChange={(e) => patch({ excludeMidlevels: e.checked ?? false })}
            />
            <label htmlFor="edit-exclude-midlevels">
              Exclude mid-levels (NP / PA / Audiology / LCSW)
            </label>
          </div>

          {taxRules.map((rule, ruleIdx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={ruleIdx}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.5rem",
                padding: "0.5rem",
                border: "1px solid var(--surface-200, #e5e7eb)",
                borderRadius: "4px",
              }}
            >
              <MultiSelect
                value={rule.payerIds}
                options={payerOptions}
                disabled={saving}
                filter
                display="chip"
                placeholder="Select payer(s)"
                style={{ minWidth: "14rem" }}
                onChange={(e) => {
                  const next = [...taxRules];
                  next[ruleIdx] = { ...rule, payerIds: e.value as number[] };
                  setTaxRules(next);
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                <Checkbox
                  inputId={`edit-taxonomy-include-all-${ruleIdx}`}
                  checked={rule.includeAll}
                  disabled={saving}
                  onChange={(e) => {
                    const next = [...taxRules];
                    next[ruleIdx] = {
                      ...rule,
                      includeAll: e.checked ?? false,
                      taxonomies: e.checked ? [] : rule.taxonomies,
                    };
                    setTaxRules(next);
                  }}
                />
                <label htmlFor={`edit-taxonomy-include-all-${ruleIdx}`}>
                  Include all
                </label>
              </div>
              <MultiSelect
                value={rule.taxonomies}
                options={taxonomyOptions}
                disabled={saving || rule.includeAll}
                filter
                display="chip"
                placeholder="Include taxonomies"
                style={{ minWidth: "16rem" }}
                onChange={(e) => {
                  const next = [...taxRules];
                  next[ruleIdx] = { ...rule, taxonomies: e.value as string[] };
                  setTaxRules(next);
                }}
              />
              <Button
                type="button"
                icon="pi pi-trash"
                className="p-button-text p-button-danger p-button-sm"
                disabled={saving}
                aria-label="Remove taxonomy rule"
                onClick={() =>
                  setTaxRules(taxRules.filter((_, i) => i !== ruleIdx))
                }
              />
            </div>
          ))}

          <Button
            type="button"
            label="Add payer taxonomy rule"
            icon="pi pi-plus"
            className="p-button-sm p-button-text"
            disabled={saving}
            onClick={() =>
              setTaxRules([
                ...taxRules,
                { payerIds: [], includeAll: false, taxonomies: [] },
              ])
            }
          />
        </div>
      )}

      {mappingSetError && (
        <small
          className="p-error"
          style={{ display: "block", marginTop: "0.75rem" }}
        >
          {mappingSetError}
        </small>
      )}
    </Dialog>
  );
}

export default EditMappingModal;
